import React, { useRef, useEffect, useMemo } from "react";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openPaytable } from "../../../../State/actions";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";

const ButtonPaytable = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig } = useDimensions();
  const { paytableActive } = useSelector((state) => state.userInterface);

  const {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  } = useButtonHover();
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const texture = getUITexture(
    paytableActive ? "icon_paytable_active" : "icon_paytable",
  );

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Center position
  const buttonPosition = useMemo(
    () => ({
      x: toolbar.width / 2,
      y: toolbar.height / 2,
    }),
    [toolbar.width, toolbar.height],
  );

  const handleClick = () => {
    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;
    soundManager.play(SOUND_NAME.button_click);
    triggerPress();

    if (!paytableActive) {
      dispatch(openPaytable());
    }

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Container
      name="menu_button_paytable"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_paytable_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={paytableActive ? t("close", { ns: "common" }) : t("paytable_title", { ns: "common" })}
          accessibleHint={paytableActive ? t("close", { ns: "common" }) : t("paytable_title", { ns: "common" })}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonPaytable;
